import type { SiteTemplateKey } from "./siteTemplates";

export interface TemplateStyleSet {
  header: {
    container: string;
    inner: string;
    logo: string;
    navWrapper: string;
  };
  footer: {
    container: string;
  };
  hero: {
    section: string;
    overlay: string;
    container: string;
    headingSize: string;
    headingColor: string;
    subheadingColor: string;
    button: string;
  };
  textGroup: {
    section: string;
    card: string;
  };
}

const base = {
  header: {
    container:
      "sticky top-0 z-40 border-b border-[var(--color-border)] bg-[var(--color-surface)]/95 backdrop-blur",
    inner: "mx-auto flex max-w-6xl items-center gap-[calc(var(--space-unit)*1)] px-4 py-[calc(var(--space-unit)*0.75)]",
    logo: "inline-flex min-h-[44px] items-center gap-2 font-semibold text-[var(--color-primary)]",
    navWrapper: "hidden items-center gap-4 md:flex",
  },
  footer: {
    container: "mt-auto border-t border-[var(--color-border)] bg-[var(--color-surface-muted)]",
  },
  hero: {
    section: "relative overflow-hidden bg-[var(--color-primary)] text-white",
    overlay: "absolute inset-0 bg-black/50",
    container:
      "relative mx-auto flex max-w-6xl flex-col gap-[calc(var(--space-unit)*1)] px-4 py-[calc(var(--space-unit)*4)] md:py-[calc(var(--space-unit)*6)]",
    headingSize: "var(--text-h1)",
    headingColor: "",
    subheadingColor: "text-white",
    button:
      "inline-flex min-h-[44px] w-fit items-center justify-center rounded-[var(--border-radius)] bg-[var(--color-surface)] px-4 py-2 text-sm font-medium text-[var(--color-text)] hover:opacity-90",
  },
  textGroup: {
    section: "mx-auto max-w-6xl px-4 py-[calc(var(--space-unit)*2.5)]",
    card: "rounded-[var(--border-radius)] border border-[var(--color-border)] bg-[var(--color-surface)] p-[calc(var(--space-unit)*1.5)] shadow-sm transition-shadow hover:shadow-md",
  },
};

export const TEMPLATE_STYLES: Record<SiteTemplateKey, TemplateStyleSet> = {
  classic: { ...base },

  editorial: {
    header: {
      container: base.header.container,
      inner:
        "mx-auto flex max-w-7xl items-end gap-[calc(var(--space-unit)*1.5)] px-4 py-[calc(var(--space-unit)*1.25)]",
      logo: "inline-flex min-h-[44px] max-w-[18rem] items-center gap-2 text-2xl font-semibold leading-tight text-[var(--color-text)]",
      navWrapper: "hidden items-end gap-6 md:flex",
    },
    footer: {
      container: base.footer.container,
    },
    hero: {
      section:
        "relative overflow-hidden border-b border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]",
      overlay: "absolute inset-0 bg-[var(--color-bg)]/70",
      container:
        "relative mx-auto grid max-w-7xl gap-[calc(var(--space-unit)*1.5)] px-4 py-[calc(var(--space-unit)*5)] md:grid-cols-[minmax(0,0.72fr)_minmax(14rem,0.28fr)] md:py-[calc(var(--space-unit)*7)]",
      headingSize: "var(--text-display)",
      headingColor: "text-[var(--color-text)]",
      subheadingColor: "text-[var(--color-secondary)]",
      button:
        "inline-flex min-h-[44px] w-fit items-center justify-center border-b border-[var(--color-primary)] py-2 text-sm font-semibold text-[var(--color-primary)]",
    },
    textGroup: {
      section: "mx-auto max-w-7xl px-4 py-[calc(var(--space-unit)*4)]",
      card: "border-t border-[var(--color-border)] bg-transparent py-[calc(var(--space-unit)*1.5)]",
    },
  },

  bold: {
    header: {
      container: "sticky top-0 z-40 bg-[var(--color-primary)] text-white",
      inner:
        "mx-auto flex max-w-7xl items-center gap-[calc(var(--space-unit)*1.5)] px-4 py-[calc(var(--space-unit)*1)]",
      logo: "inline-flex min-h-[44px] items-center gap-2 text-xl font-bold text-white",
      navWrapper: "hidden items-center gap-6 md:flex",
    },
    footer: {
      container:
        "mt-auto border-t-4 border-[var(--color-primary)] bg-[var(--color-text)] text-[var(--color-surface)]",
    },
    hero: {
      section:
        "relative overflow-hidden bg-[var(--color-primary)] text-white",
      overlay: "absolute inset-0 bg-black/50",
      container:
        "relative mx-auto grid max-w-7xl gap-[calc(var(--space-unit)*2)] px-4 py-[calc(var(--space-unit)*5)] md:grid-cols-2 md:items-center md:py-[calc(var(--space-unit)*8)]",
      headingSize: "var(--text-display)",
      headingColor: "",
      subheadingColor: "text-white",
      button:
        "inline-flex min-h-[44px] w-fit items-center justify-center rounded-[var(--border-radius)] border-2 border-white px-6 py-2 text-sm font-semibold text-white hover:bg-white hover:text-[var(--color-primary)]",
    },
    textGroup: {
      section: "mx-auto max-w-7xl px-4 py-[calc(var(--space-unit)*4)]",
      card: "rounded-[var(--border-radius)] border-l-4 border-[var(--color-primary)] bg-[var(--color-surface)] p-[calc(var(--space-unit)*1.5)] shadow-sm",
    },
  },

  minimal: {
    header: {
      container:
        "sticky top-0 z-40 border-b border-[var(--color-border)] bg-[var(--color-surface)]",
      inner:
        "mx-auto flex max-w-5xl items-center gap-[calc(var(--space-unit)*1)] px-4 py-[calc(var(--space-unit)*0.5)]",
      logo: "inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[var(--color-text)]",
      navWrapper: "hidden items-center gap-6 md:flex",
    },
    footer: {
      container:
        "mt-auto border-t border-[var(--color-border)] bg-[var(--color-surface)]",
    },
    hero: {
      section:
        "relative overflow-hidden border-b border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)]",
      overlay: "",
      container:
        "relative mx-auto flex max-w-5xl flex-col gap-[calc(var(--space-unit)*1)] px-4 py-[calc(var(--space-unit)*6)] md:py-[calc(var(--space-unit)*10)]",
      headingSize: "var(--text-display)",
      headingColor: "text-[var(--color-text)]",
      subheadingColor: "text-[var(--color-secondary)]",
      button:
        "inline-flex min-h-[44px] w-fit items-center justify-center border border-[var(--color-text)] px-6 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-text)] hover:text-[var(--color-surface)]",
    },
    textGroup: {
      section: "mx-auto max-w-5xl px-4 py-[calc(var(--space-unit)*3)]",
      card: "border-b border-[var(--color-border)] bg-transparent py-[calc(var(--space-unit)*1.5)]",
    },
  },

  tranquil: {
    header: {
      container:
        "sticky top-0 z-40 border-b border-[var(--color-border)] bg-[var(--color-surface)]/90 backdrop-blur",
      inner:
        "mx-auto flex max-w-6xl items-center gap-[calc(var(--space-unit)*1.25)] px-4 py-[calc(var(--space-unit)*1)]",
      logo: "inline-flex min-h-[44px] items-center gap-2 text-lg font-semibold tracking-wide text-[var(--color-primary)]",
      navWrapper: "hidden items-center gap-5 md:flex",
    },
    footer: {
      container:
        "mt-auto border-t border-[var(--color-border)] bg-[var(--color-surface-muted)]",
    },
    hero: {
      section:
        "relative overflow-hidden bg-[var(--color-surface-muted)] text-[var(--color-text)]",
      overlay: "absolute inset-0 bg-[var(--color-bg)]/35",
      container:
        "relative mx-auto flex max-w-5xl flex-col gap-[calc(var(--space-unit)*1.25)] px-4 py-[calc(var(--space-unit)*5)] md:py-[calc(var(--space-unit)*8)]",
      headingSize: "var(--text-display)",
      headingColor: "text-[var(--color-text)]",
      subheadingColor: "text-[var(--color-secondary)]",
      button:
        "inline-flex min-h-[44px] w-fit items-center justify-center rounded-full bg-[var(--color-primary)] px-6 py-2 text-sm font-medium text-white hover:opacity-90",
    },
    textGroup: {
      section: "mx-auto max-w-6xl px-4 py-[calc(var(--space-unit)*3.5)]",
      card: "rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-[calc(var(--space-unit)*1.75)] shadow-sm",
    },
  },
};

export function getTemplateStyles(template: SiteTemplateKey): TemplateStyleSet {
  return TEMPLATE_STYLES[template] ?? TEMPLATE_STYLES.classic;
}
